import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, CircleCheck, CircleMinus } from 'lucide-react';
import type { TicketDetailData } from '@/entities/ticket';
import {
  directoryValues,
  formFileValues,
  formatFormValue,
  isDirectoryField,
  userFieldValues,
  type DirectoryValue,
  type FormField,
} from '@/shared/api/forms';
import { DirectoryEntryDialog } from '@/features/tickets/ui/DirectoryEntryDialog';
import { onExternalLinkClick } from '@/shared/lib/external-link';
import { formatPhone } from '@/shared/lib/phone';
import { cn } from '@/shared/lib/cn';
import { Avatar } from '@/shared/ui';
import { groupAnswers } from './lib';
import styles from './TicketDetail.module.css';

function formatDate(value: string): string {
  const d = new Date(`${value}T00:00:00`);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric', weekday: 'short' });
}

const AnswerValue: React.FC<{
  field: FormField;
  value: unknown;
  onDirectory: (value: DirectoryValue, directoryId: string | null) => void;
}> = ({ field, value, onDirectory }) => {
  if (isDirectoryField(field)) {
    const items = directoryValues(value);
    if (items.length === 1) {
      const [item] = items;
      return (
        <button
          type="button"
          className={styles.answerLink}
          onClick={() => onDirectory(item, field.source?.directoryId ?? null)}
        >
          {item.label}
          <ChevronRight size={14} aria-hidden className={styles.answerLinkIcon} />
        </button>
      );
    }
    return (
      <span className={styles.answerChips}>
        {items.map((item, i) => (
          <button
            key={`${item.id ?? item.label}-${i}`}
            type="button"
            className={cn(styles.answerChip, styles.answerChipButton)}
            onClick={() => onDirectory(item, field.source?.directoryId ?? null)}
          >
            {item.label}
          </button>
        ))}
      </span>
    );
  }
  switch (field.type) {
    case 'user':
      return (
        <span className={styles.answerChips}>
          {userFieldValues(value).map((person, i) => {
            const key = `${person.id ?? person.label}-${i}`;
            const avatar = <Avatar name={person.label} seed={person.id ?? person.label} className={styles.answerAvatar} />;
            const number = person.personnelNumber && (
              <span className={styles.answerChipNote}>таб. № {person.personnelNumber}</span>
            );
            if (person.id && !person.external) {
              return (
                <Link key={key} to={`/users/${person.id}`} className={cn(styles.answerChip, styles.answerChipButton)}>
                  {avatar}
                  {person.label}
                  {number}
                </Link>
              );
            }
            return (
              <span key={key} className={styles.answerChip}>
                {avatar}
                {person.label}
                {number}
                {person.external && <span className={styles.answerChipNote}>нет в системе</span>}
              </span>
            );
          })}
        </span>
      );
    case 'multiselect':
      return (
        <span className={styles.answerChips}>
          {(value as string[]).map((v) => (
            <span key={v} className={styles.answerChip}>
              {field.options?.find((o) => o.value === v)?.label ?? v}
            </span>
          ))}
        </span>
      );
    case 'date':
      return <>{formatDate(String(value))}</>;
    case 'email':
      return <a href={`mailto:${String(value)}`}>{String(value)}</a>;
    case 'phone':
      return <a href={`tel:${String(value).replace(/[^\d+]/g, '')}`}>{formatPhone(String(value))}</a>;
    case 'url': {
      const url = String(value);
      let shown = url;
      try {
        shown = decodeURI(url.replace(/^https?:\/\//, ''));
      } catch {
      }
      return (
        <a
          href={url}
          target="_blank"
          rel="noreferrer"
          title={url}
          className={styles.answerUrl}
          onClick={onExternalLinkClick(url)}
        >
          {shown}
        </a>
      );
    }
    case 'file':
      return (
        <>
          {formFileValues(value).map((file, i) => (
            <span key={file.id}>
              {i > 0 && <span className={styles.answerSep}> · </span>}
              {file.url ? (
                <a href={file.url} target="_blank" rel="noreferrer">
                  {file.name}
                </a>
              ) : (
                file.name
              )}
            </span>
          ))}
        </>
      );
    default:
      return <>{formatFormValue(field, value)}</>;
  }
};

const WIDE = new Set(['multiselect', 'textarea', 'folder', 'group', 'file', 'url']);

function isWide(field: FormField, value: unknown): boolean {
  if (field.type === 'user') return userFieldValues(value).length > 1;
  if (isDirectoryField(field)) return directoryValues(value).length > 1;
  return WIDE.has(field.type);
}

export const FormAnswers: React.FC<{ ticket: TicketDetailData }> = ({ ticket }) => {
  const [card, setCard] = useState<{ value: DirectoryValue; directoryId: string | null } | null>(null);
  const fields = ticket.formSchema?.fields;
  const data = ticket.formData;
  if (!fields || !data) return null;
  const groups = groupAnswers(fields, data);
  if (groups.length === 0) return null;
  return (
    <div className={styles.answers}>
      {groups.map((group, gi) => {
        const cells = group.fields.filter((f) => f.type !== 'checkbox');
        const checks = group.fields.filter((f) => f.type === 'checkbox');
        return (
          <section key={group.title ?? `g${gi}`} className={styles.answersGroup}>
            {group.title && <h3 className={styles.answersTitle}>{group.title}</h3>}
            {cells.length > 0 && (
              <dl className={styles.answersList}>
                {cells.map((field) => (
                  <div key={field.key} className={cn(styles.answerCell, isWide(field, data[field.key]) && styles.answerCellWide)}>
                    <dt className={styles.answerLabel}>{field.label}</dt>
                    <dd className={styles.answerValue}>
                      <AnswerValue
                        field={field}
                        value={data[field.key]}
                        onDirectory={(value, directoryId) => setCard({ value, directoryId })}
                      />
                    </dd>
                  </div>
                ))}
              </dl>
            )}
            {checks.length > 0 && (
              <ul className={styles.answerChecks}>
                {checks.map((field) => {
                  const on = data[field.key] === true;
                  const Icon = on ? CircleCheck : CircleMinus;
                  return (
                    <li key={field.key} className={cn(styles.answerCheck, !on && styles.answerCheckOff)}>
                      <Icon size={16} aria-hidden className={styles.answerCheckIcon} />
                      <span>
                        {field.label}
                        {!on && <span className={styles.srOnly}> — нет</span>}
                      </span>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        );
      })}
      <DirectoryEntryDialog
        open={card != null}
        value={card?.value ?? null}
        directoryId={card?.directoryId ?? null}
        ticketId={ticket.id}
        onClose={() => setCard(null)}
      />
    </div>
  );
};
